import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import { lt } from '@mews/framework';
import { Stack } from '@optimus-web/core';
import { DataTestId } from '@mews-commander/enums';
import { Option } from '@mews-commander/types';
import { SelectField } from '@mews-commander/form';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import {
    getEnterpriseServiceOptions,
    hasMultipleBookableServices,
} from '@mews-commander/legacy-entities/src/ducks/services';
import { hasUniqueServiceResourceFloorNumbers } from '@mews-commander/legacy-entities/src/selectors/spaces';
import { getResourceCategoriesOptions } from '@mews-commander/legacy-entities/src/ducks/resourceCategories';
import { getResourceFeatureOptions } from '@mews-commander/legacy-entities/src/ducks/resourceFeatures';
import { useCommanderSelector } from '@mews-commander/utils';
import { getServiceFloorOptions } from '../../../resources/selectors';
import { TimelineFilterSchemaKeys, TimelineFilterValues } from './timelineFilterSchema';
import { useTimelineFilterPreselectedValues } from './useTimelineFilterDefaultValues';

export const TimelineFilterFields: React.FC<{ currentServiceId: string }> = ({ currentServiceId }) => {
    const enterpriseId = useCurrentEnterpriseId();

    const { watch, reset } = useFormContext<TimelineFilterValues>();
    const serviceId = watch(TimelineFilterSchemaKeys.ServiceId);
    const newDefaultFilterValues = useTimelineFilterPreselectedValues(serviceId);

    useEffect(() => {
        if (serviceId !== currentServiceId) {
            reset(newDefaultFilterValues);
        }
    }, [currentServiceId, newDefaultFilterValues, reset, serviceId]);

    const hasMultipleServices = useCommanderSelector((state) => hasMultipleBookableServices(state, { enterpriseId }));
    const uniqueServiceFloorNumbers = useCommanderSelector((state) =>
        hasUniqueServiceResourceFloorNumbers(state, { serviceId })
    );

    const serviceOptions: Option[] = useCommanderSelector((state) =>
        getEnterpriseServiceOptions(state, { enterpriseId })
    );
    const resourceCategoryOptions: Option[] = useCommanderSelector((state) =>
        getResourceCategoriesOptions(state, { serviceId })
    );
    const floorSelectOptions: Option[] = useCommanderSelector((state) => getServiceFloorOptions(state, { serviceId }));
    const resourceFeatureOptions: Option[] = useCommanderSelector(getResourceFeatureOptions);

    return (
        <Stack vertical spacing="300">
            {hasMultipleServices && (
                <SelectField
                    required
                    name={TimelineFilterSchemaKeys.ServiceId}
                    label={lt('SelectService')}
                    options={serviceOptions}
                    data-test-id={DataTestId.ServiceSelector}
                />
            )}
            <SelectField
                multiselect
                compact
                name={TimelineFilterSchemaKeys.FloorNumber}
                label={lt('Floor')}
                disabled={!uniqueServiceFloorNumbers}
                options={floorSelectOptions}
            />
            <SelectField
                multiselect
                compact
                name={TimelineFilterSchemaKeys.ResourceFeatureId}
                label={lt('SpaceFeature')}
                options={resourceFeatureOptions}
            />
            <SelectField
                multiselect
                compact
                name={TimelineFilterSchemaKeys.ResourceCategoryId}
                label={lt('ResourceCategory')}
                options={resourceCategoryOptions}
            />
        </Stack>
    );
};
